import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Icon } from '@iconify/react';
import CONFIG from '../../config';
import Swal from 'sweetalert2';
import LoadingSpinner from '../common/LoadingSpinner';

const RunControl = ({ project, onStartRun, onStopRun }) => {
    const [batchStats, setBatchStats] = useState({ total_in_queue: 0, batches: {} });
    const [loading, setLoading] = useState(true);
    const [isRaceActive, setIsRaceActive] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [devices] = useState([
        { name: 'RFID Master (Start)', status: 'online', icon: 'ion:wifi-outline' },
        { name: 'RFID Slave (Lap 1)', status: 'online', icon: 'ion:wifi-outline' },
        { name: 'Timing Mat (Finish)', status: 'offline', icon: 'ion:radio-outline' },
        { name: 'Camera Host', status: 'online', icon: 'ion:videocam-outline' },
    ]);

    useEffect(() => {
        if (!project) return;
        fetchBatchStats();
        checkRaceStatus();
        const interval = setInterval(() => {
            fetchBatchStats();
            checkRaceStatus();
        }, 5000);
        return () => clearInterval(interval);
    }, [project]);

    const fetchBatchStats = async () => {
        try {
            const token = localStorage.getItem('token');
            const resp = await axios.get(`${CONFIG.API_URL}/api/candidates/batch-stats/${project._id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setBatchStats(resp.data);
        } catch (err) {
            console.error('Failed to fetch batch stats:', err);
        } finally {
            setLoading(false);
        }
    };

    // Check if a race is currently active by pinging live-run
    const checkRaceStatus = async () => {
        try {
            const token = localStorage.getItem('token');
            const resp = await axios.get(`${CONFIG.API_URL}/api/candidates/live-run/${project._id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setIsRaceActive(!!resp.data.run_start_time);
        } catch (err) {
            setIsRaceActive(false);
        }
    };

    const handleStartRun = async () => {
        // Guard: block start if TRACKER stage has no candidates ready
        if (batchStats.total_in_queue === 0) {
            Swal.fire({
                title: 'No Candidates Available',
                html: `<p>There are <strong>no candidates</strong> currently in the TRACKER (running) stage for this project.</p>
                       <p style="margin-top:8px;color:#6b7280;font-size:13px">Use the Master Admin simulator to move candidates to the TRACKER stage before starting a race.</p>`,
                icon: 'warning',
                confirmButtonColor: '#800000',
                confirmButtonText: 'Understood'
            });
            return;
        }

        const result = await Swal.fire({
            title: 'Confirm Race Start',
            text: `Starting run for ${batchStats.total_in_queue} candidates. This will sync all timing mats.`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'START NOW',
            confirmButtonColor: '#10b981',
            cancelButtonColor: '#6b7280'
        });

        if (result.isConfirmed) {
            setActionLoading(true);
            try {
                const token = localStorage.getItem('token');
                await axios.post(`${CONFIG.API_URL}/api/candidates/start-run`, {
                    projectId: project._id,
                    batchId: Object.keys(batchStats.batches)[0]
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setIsRaceActive(true);
                Swal.fire({ title: 'Race Active!', text: 'Timing engine started.', icon: 'success', timer: 1800, showConfirmButton: false });
                if (onStartRun) onStartRun();
            } catch (err) {
                Swal.fire('Error', 'Failed to start race engine.', 'error');
            } finally {
                setActionLoading(false);
            }
        }
    };

    const handleStopRaceFromControl = async () => {
        const result = await Swal.fire({
            title: 'Stop Race?',
            text: 'This will stop the timer and reset all candidate statuses.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, Stop Race',
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#6b7280'
        });

        if (result.isConfirmed) {
            setActionLoading(true);
            try {
                const token = localStorage.getItem('token');
                await axios.post(`${CONFIG.API_URL}/api/candidates/stop-run`, {
                    projectId: project._id
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setIsRaceActive(false);
                Swal.fire({ title: 'Race Stopped', icon: 'success', timer: 1500, showConfirmButton: false });
                if (onStopRun) onStopRun();
                fetchBatchStats();
            } catch (err) {
                Swal.fire('Error', err.response?.data?.message || 'Failed to stop race.', 'error');
            } finally {
                setActionLoading(false);
            }
        }
    };

    if (loading && batchStats.total_in_queue === 0) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '100px' }}>
                <LoadingSpinner message="Checking System Readiness..." />
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
            {/* Header */}
            <div>
                <h2 style={{ fontSize: '26px', fontWeight: 900, margin: 0 }}>
                    {isRaceActive ? '🟢 Race In Progress' : 'Run Control'}
                </h2>
                <p style={{ color: 'var(--text-muted)', marginTop: '4px', fontWeight: 500 }}>
                    {isRaceActive
                        ? 'A race is currently active. Navigate to Live Race to monitor it, or stop it below.'
                        : `Manage and start the running test for ${project?.name}.`
                    }
                </p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px' }}>
                {/* Batch Readiness */}
                <div className="ro-stat-card">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                        <h3 className="ro-stat-label">Batch Readiness</h3>
                        <div style={{
                            padding: '4px 12px',
                            background: batchStats.total_in_queue > 0 ? '#dcfce7' : '#fef3c7',
                            color: batchStats.total_in_queue > 0 ? '#166534' : '#92400e',
                            borderRadius: '20px', fontSize: '12px', fontWeight: 900
                        }}>
                            {batchStats.total_in_queue > 0 ? 'READY' : 'EMPTY'}
                        </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                        <div style={{ width: '80px', height: '80px', background: 'rgba(128,0,0,0.05)', borderRadius: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--primary)' }}>
                            <Icon icon="ion:people-outline" width="40" />
                        </div>
                        <div>
                            <div style={{ fontSize: '48px', fontWeight: 900 }}>{batchStats.total_in_queue}</div>
                            <div style={{ color: 'var(--text-muted)', fontWeight: 700 }}>Candidates in Track Queue</div>
                        </div>
                    </div>
                    <div style={{ marginTop: '24px', borderTop: '1px solid #f1f5f9', paddingTop: '16px' }}>
                        <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-muted)' }}>Current Batches:</div>
                        <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
                            {Object.entries(batchStats.batches).length === 0
                                ? <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>No batches available</span>
                                : Object.entries(batchStats.batches).map(([id, count]) => (
                                    <div key={id} style={{ padding: '6px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '12px', fontWeight: 800 }}>
                                        {id}: {count}
                                    </div>
                                ))
                            }
                        </div>
                    </div>
                </div>

                {/* Device Health */}
                <div className="ro-stat-card">
                    <h3 className="ro-stat-label">System Health Check</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        {devices.map(dev => (
                            <div key={dev.name} style={{ padding: '12px', background: '#f8fafc', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <Icon icon={dev.icon} width="20" color={dev.status === 'online' ? '#10b981' : '#ef4444'} />
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: '11px', fontWeight: 800 }}>{dev.name}</div>
                                    <div style={{ fontSize: '10px', fontWeight: 700, color: dev.status === 'online' ? '#10b981' : '#ef4444', textTransform: 'uppercase' }}>{dev.status}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                    {devices.some(d => d.status === 'offline') && (
                        <div style={{ marginTop: '16px', color: '#ef4444', fontSize: '12px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Icon icon="ion:warning" /> Warning: Finishing mat disconnected.
                        </div>
                    )}
                </div>
            </div>

            {/* Action Button: START or STOP based on race state */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', marginTop: '16px' }}>
                {isRaceActive ? (
                    <>
                        <button
                            className="ro-btn-start"
                            onClick={() => onStartRun && onStartRun()}
                            style={{ background: '#0ea5e9', boxShadow: '0 20px 40px rgba(14,165,233,0.3)' }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                <Icon icon="ion:eye" />
                                VIEW LIVE RACE
                            </div>
                        </button>
                        <button
                            onClick={handleStopRaceFromControl}
                            disabled={actionLoading}
                            style={{
                                padding: '16px 48px',
                                background: 'white',
                                border: '2px solid #ef4444',
                                color: '#ef4444',
                                borderRadius: '16px',
                                fontWeight: 800,
                                fontSize: '16px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                transition: 'all 0.2s'
                            }}
                            onMouseEnter={e => { e.currentTarget.style.background = '#fef2f2'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = 'white'; }}
                        >
                            <Icon icon="ion:stop-circle" width="20" />
                            STOP RACE & RESET TIMER
                        </button>
                    </>
                ) : (
                    <>
                        <button
                            className="ro-btn-start"
                            onClick={handleStartRun}
                            disabled={loading || actionLoading || batchStats.total_in_queue === 0}
                            style={{
                                opacity: batchStats.total_in_queue === 0 ? 0.5 : 1,
                                cursor: batchStats.total_in_queue === 0 ? 'not-allowed' : 'pointer'
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                <Icon icon="ion:play" />
                                {batchStats.total_in_queue === 0 ? 'NO CANDIDATES READY' : 'START RUN'}
                            </div>
                        </button>
                        {batchStats.total_in_queue === 0 && (
                            <div style={{ color: '#ef4444', fontSize: '13px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Icon icon="ion:warning" width="16" />
                                Move candidates to TRACKER stage first to enable start.
                            </div>
                        )}
                    </>
                )}
                <div style={{ color: 'var(--text-muted)', fontWeight: 700, fontSize: '14px', textAlign: 'center' }}>
                    {isRaceActive
                        ? 'Race is live. Use the Live Race tab or Stop Race to manage it.'
                        : 'Click to synchronize all readers and begin the active tracking session.'}
                </div>
            </div>
        </div>
    );
};

export default RunControl;
